import * as React from "react";
import { Form, useFormApi } from "informed";
import { nameof } from 'ts-simple-nameof';
import { Checkbox } from "../../../forms/informedv2/Checkbox";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { DcfRequestStatusEnum, StudyDcfSetting } from "../../../misc/api/apiTypes";
import { Link, useParams } from "react-router-dom";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";

interface DcfSettingsFormState {
    VerificationApprovalEnabled: boolean;
    AutoImplementationEnabled: boolean;
}

export interface DcfSettingsFormProps {
    setSuccess: boolean;
}

declare const rootUrl: string;

const dcfVerificationApprovalEnabledSetting = "Vitalograph.WebPortal.Data.Settings.DcfVerificationApprovalEnabledSetting";
const studyDcfSetting = "Vitalograph.WebPortal.Data.Settings.StudyDcfSetting";

export const DcfSettingsForm = ({ setSuccess, studyId }) => {
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const formApi = useFormApi<DcfSettingsFormState>();
    const [errorAlert, setErrorAlert] = React.useState<string>();

    const handleSubmit = async () => {
        const state = formApi.getState();

        if (state.values.VerificationApprovalEnabled === false) {
            const dcfRequestsInVerificationReview = await client.client.getDcfRequests(undefined, `studyId eq ${studyId} and status eq '${DcfRequestStatusEnum.VerificationReview}'`);
            if (dcfRequestsInVerificationReview.value.length > 0) {
                formApi.setValue("VerificationApprovalEnabled", true);
                return setErrorAlert("Data Corrections with the Verification Approval status currently exist, cannot be turned off");
            }
        }
        const response1 = await legacyClient.putStudySetting(studyId, dcfVerificationApprovalEnabledSetting, state.values.VerificationApprovalEnabled);

        const dcfSettings: StudyDcfSetting = {
            AutoImplementationEnabled: state.values.AutoImplementationEnabled,
        }
        const payload = JSON.stringify(dcfSettings);
        const response2 = await legacyClient.putStudySetting(studyId, studyDcfSetting, payload);

        setSuccess(response1 && response2);
    }

    return (
        <>
            <Alert show={errorAlert !== undefined} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="panel panel-default">
                <div className="panel-body">
                    <div className="row">
                        <div className="col-sm-6">
                            <div className="form-group">
                                <Checkbox
                                    id="VerificationApproval"
                                    label="Verification Approval Enabled"
                                    field={nameof<DcfSettingsFormState>(f => f.VerificationApprovalEnabled)}
                                />
                            </div>
                        </div>
                        <div className="col-sm-6">
                            <div className="form-group">
                                <Checkbox
                                    id="AutoImplementation"
                                    label="Auto-implementation Enabled"
                                    field={nameof<DcfSettingsFormState>(f => f.AutoImplementationEnabled)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12 pull-right">
                    <button className="btn btn-primary" type="submit" onClick={handleSubmit}>Save</button>
                </div>
            </div>
        </>
    )
}

export const DcfSettings = () => {
    const { id } = useParams<{ id: string }>();
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [initialValues, setInitialValues] = React.useState<DcfSettingsFormState | undefined>();
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const getSetting = async () => {
            const settingsQuery = await legacyClient.getSettingsHelper(dcfVerificationApprovalEnabledSetting, id);
            const dcfSettings = await legacyClient.getStudyDcfSettings(id);

            setInitialValues({
                VerificationApprovalEnabled: settingsQuery.Data.length > 0 ? settingsQuery.Data[0].Value === "True" : false,
                AutoImplementationEnabled: dcfSettings.AutoImplementationEnabled,
            });
        }

        getSetting();
    }, [id]);

    if (!initialValues)
        return <LoadingWithMessage message="Loading DCF Settings..." />

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to <Link to="/studies">Studies</Link>?
            </Alert>
            <div className="row">
                <div className="col-md-12">
                    <h2 className="text-primary">DCF Settings</h2>
                </div>
            </div>
            <div id="dcfSettings">
                <Form<DcfSettingsFormState> initialValues={initialValues}>
                    <DcfSettingsForm setSuccess={setSuccessAlert} studyId={id} />
                </Form>
            </div>
        </>
    );
}
